import config, { logger } from '../config';
import { getApiHeaders } from './api';

const withSessionEmail = (data, email) => {
  if (!email || !data?.data || data.data.email) return data;
  return { ...data, data: { ...data.data, email } };
};

const parseSocialLoginError = (data, status) => {
  const message = data?.message || `Login failed (${status})`;
  const error = new Error(message);
  if (data?.error_code) {
    error.errorCode = data.error_code;
  }
  return error;
};

const parseSocialLoginResponse = async (backendResponse) => {
  let data = {};
  try {
    data = await backendResponse.json();
  } catch (parseError) {
    logger.error('Failed to parse social login response:', parseError);
  }

  if (!backendResponse.ok || !data.success) {
    throw parseSocialLoginError(data, backendResponse.status);
  }

  return data;
};

const assertGoogleSdk = () => {
  if (typeof window.google === 'undefined' || !window.google.accounts) {
    throw new Error('Google Sign-In not available. Please ensure Google Sign-In SDK is loaded.');
  }
};

/**
 * Opens the Google popup and returns the session without creating an account.
 * @returns {Promise<{ accessToken: string, fullName: string, email: string, profilePicture: string }>}
 */
export const requestGoogleSession = async () => {
  try {
    assertGoogleSdk();

    return await new Promise((resolve, reject) => {
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: config.SOCIAL_LOGIN.GOOGLE_CLIENT_ID,
        scope: 'email profile openid',
        redirect_uri: window.location.origin,
        callback: async (response) => {
          if (response.error) {
            logger.error('Google Sign-In error:', response.error);
            reject(new Error('Google Sign-In failed: ' + response.error));
            return;
          }

          try {
            const userInfoResponse = await fetch(`https://www.googleapis.com/oauth2/v2/userinfo?access_token=${response.access_token}`);

            if (!userInfoResponse.ok) {
              throw new Error(`Failed to fetch user info: ${userInfoResponse.status} ${userInfoResponse.statusText}`);
            }

            const userInfo = await userInfoResponse.json();
            if (!userInfo.email) {
              throw new Error('Email not provided by Google. Cannot create or link account.');
            }

            resolve({
              accessToken: response.access_token,
              fullName: userInfo.name || '',
              email: userInfo.email,
              profilePicture: userInfo.picture || '',
            });
          } catch (error) {
            logger.error('Error processing Google response:', error);
            reject(error);
          }
        },
      });

      tokenClient.requestAccessToken();
    });
  } catch (error) {
    logger.error('Google Sign-In error:', error);
    throw error;
  }
};

/**
 * Completes Google login or account creation with an existing Google session.
 * @param {{ accessToken: string, fullName?: string, email?: string, profilePicture?: string, extendedWelcomeBlocker?: string }} session
 */
export const submitGoogleLogin = async (session) => {
  const body = {
    access_token: session.accessToken,
    full_name: session.fullName,
    email: session.email,
    profile_picture: session.profilePicture,
  };
  if (session.extendedWelcomeBlocker) {
    body.extended_welcome_blocker = session.extendedWelcomeBlocker;
  }

  const backendResponse = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.GOOGLE_LOGIN}`, {
    method: 'POST',
    headers: getApiHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(body),
  });

  return withSessionEmail(await parseSocialLoginResponse(backendResponse), session.email);
};

// Google Sign-In implementation
export const handleGoogleLogin = async ({ extendedWelcomeBlocker } = {}) => {
  const session = await requestGoogleSession();
  return submitGoogleLogin({ ...session, extendedWelcomeBlocker });
};

// Facebook Login implementation
export const handleFacebookLogin = () => {
  return new Promise((resolve, reject) => {
    try {
      // Check if Facebook SDK is available
      if (typeof window.FB === 'undefined') {
        reject(new Error('Facebook SDK not available. Please ensure Facebook SDK is loaded.'));
        return;
      }

      window.FB.login((response) => {
        if (response.authResponse) {
          try {

            // Get user info
            window.FB.api('/me', { fields: 'name,email,picture' }, async (userInfo) => {
              if (userInfo.error) {
                logger.error('Facebook API error:', userInfo.error);
                reject(new Error('Failed to get Facebook user info: ' + userInfo.error.message));
                return;
              }

              try {
                // Send to backend
                const backendResponse = await fetch(`${config.API_BASE_URL}${config.API_ENDPOINTS.FACEBOOK_LOGIN}`, {
                  method: 'POST',
                  headers: getApiHeaders({ 'Content-Type': 'application/json' }),
                  body: JSON.stringify({
                    access_token: response.authResponse.accessToken,
                    full_name: userInfo.name,
                    email: userInfo.email,
                    profile_picture: userInfo.picture?.data?.url
                  }),
                });

                const data = withSessionEmail(await parseSocialLoginResponse(backendResponse), userInfo.email);

                resolve(data);
              } catch (error) {
                logger.error('Error sending to backend:', error);
                reject(error);
              }
            });
          } catch (error) {
            logger.error('Error in Facebook API call:', error);
            reject(error);
          }
        } else {
          reject(new Error('Facebook login was cancelled or failed'));
        }
      }, { scope: 'email,public_profile' });
    } catch (error) {
      logger.error('Facebook login initialization error:', error);
      reject(error);
    }
  });
};
